/**
 * Navigation Helpers - Extracted from useAuth hook for better maintainability
 * Handles credential validation and navigation logic after login
 * Uses encrypted storage for sensitive data (OWASP A02: Cryptographic Failures mitigation)
 */

import { integrationService } from '../../../shared/integration/services/IntegrationService';
import logger from '../../../shared/services/logging/logger';
import { secureStorage } from '../../../shared/utils/secureStorage';

// Constants
export const NAVIGATION_CONSTANTS = {
  NAVIGATION_DELAY_MS: 100,
  PROCESSING_DELAY_MS: 1500
};

/**
 * Check if user needs integration setup.
 * On API failure (e.g. 401 before tokens are fully loaded) returns needsSetup: false
 * so the user lands on the dashboard rather than getting stuck in a setup loop.
 */
export const checkIntegrationSetup = async (organizationId) => {
  try {
    const needsSetup = await integrationService.needsSetup(organizationId);
    return { success: true, needsSetup };
  } catch (error) {
    logger.error('Error checking integration setup', {
      organizationId,
      error: error.message
    });
    // On failure, default to false — let the user reach the dashboard.
    // IntegrationSetupWrapper handles the setup check with proper hydration guards.
    return { success: false, needsSetup: false, error };
  }
};

/**
 * Validate AWS/Bedrock configuration
 */
export const validateAWSConfig = async (organizationId) => {
  try {
    const awsConfigResult = await integrationService.getAWSConfig(organizationId);

    if (awsConfigResult.success && awsConfigResult.data) {
      const awsConfig = awsConfigResult.data;
      const configType = awsConfig.customBuckets ? 'Custom buckets' : 'Tonic buckets';
      const bedrockType = awsConfig.customGenAi ? 'Custom Bedrock' : 'Tonic Bedrock';

      logger.info(`AWS: ${configType} - VALID`);
      logger.info(`BEDROCK: ${bedrockType} - VALID`);

      return {
        success: true,
        isValid: true,
        config: awsConfig
      };
    } else {
      logger.warn('Could not fetch AWS config - assuming Tonic defaults (VALID)');
      return {
        success: false,
        isValid: true, // Assume valid if can't fetch
        config: null
      };
    }
  } catch (error) {
    logger.error('Error validating AWS config', {
      organizationId,
      error: error.message
    });
    return {
      success: false,
      isValid: true, // Don't block on AWS config errors
      config: null
    };
  }
};

/**
 * Check if integration has actual credentials supplied
 */
const hasSuppliedCredentials = (integration) => {
  if (integration.integrationType === 'ATLASSIAN') {
    return (
      (integration.emailSupplied && integration.emailSupplied !== 'not_supplied') ||
      (integration.apiTokenSupplied && integration.apiTokenSupplied !== 'not_supplied') ||
      (integration.siteUrlSupplied && integration.siteUrlSupplied !== 'not_supplied')
    );
  }

  if (integration.integrationType === 'XRAY') {
    return (
      (integration.clientIdSupplied && integration.clientIdSupplied !== 'not_supplied') ||
      (integration.clientSecretSupplied && integration.clientSecretSupplied !== 'not_supplied')
    );
  }

  return false;
};

/**
 * Validate a single integration's credentials
 */
const validateIntegration = (integration) => {
  logger.debug(`Checking integration: ${integration.integrationType}`, {
    isActive: integration.isActive,
    verificationStatus: integration.verificationStatus
  });

  // Only check optional integrations (Atlassian, XRAY)
  if (integration.integrationType !== 'ATLASSIAN' && integration.integrationType !== 'XRAY') {
    return null; // Skip non-optional integrations
  }

  const hasActualCredentials = hasSuppliedCredentials(integration);

  logger.debug(`${integration.integrationType} hasActualCredentials: ${hasActualCredentials}`);

  // If credentials were supplied but are invalid, mark as invalid
  if (hasActualCredentials && integration.verificationStatus === 'invalid') {
    logger.error(`${integration.integrationType} has SUPPLIED but INVALID credentials - BLOCKING navigation`);
    return {
      type: integration.integrationType,
      reason: 'Supplied credentials are invalid',
      mandatory: false
    };
  }

  if (hasActualCredentials && integration.verificationStatus === 'valid') {
    logger.info(`${integration.integrationType} has supplied credentials and they are valid`);
  } else if (!hasActualCredentials) {
    logger.info(`${integration.integrationType} credentials not supplied - OK, skipping`);
  }

  return null; // Valid or no credentials
};

/**
 * Validate all Atlassian/XRAY integrations
 */
export const validateIntegrations = async (organizationId) => {
  try {
    const integrationsResult = await integrationService.getAllIntegrations(organizationId);

    if (!integrationsResult.success || !integrationsResult.data) {
      logger.warn('Could not fetch integrations data');
      return { success: false, invalidIntegrations: [] };
    }

    logger.info('Checking Atlassian/XRAY integrations', {
      count: integrationsResult.data.length
    });

    const invalidIntegrations = integrationsResult.data
      .map(validateIntegration)
      .filter(result => result !== null);

    return {
      success: true,
      invalidIntegrations
    };
  } catch (error) {
    logger.error('Error validating integrations', {
      organizationId,
      error: error.message
    });
    return {
      success: false,
      invalidIntegrations: [],
      error
    };
  }
};

/**
 * Check if user has admin role
 */
export const isAdminUser = (userRoles = []) => {
  return userRoles.some(role =>
    role.toUpperCase() === 'ROLE_ADMIN' || role.toUpperCase() === 'ADMIN'
  );
};

/**
 * Handle invalid credentials by redirecting to settings
 * Stores sensitive role and integration data using encryption (OWASP A02 mitigation)
 */
export const handleInvalidCredentials = (invalidIntegrations, userRoles, navigate) => {
  logger.warn('Invalid integrations detected', {
    count: invalidIntegrations.length,
    integrations: invalidIntegrations
  });
  logger.error('BLOCKING NAVIGATION - Redirecting to organization settings');

  const isAdmin = isAdminUser(userRoles);

  // Store in encrypted storage for settings page (contains sensitive role/integration data)
  secureStorage.setEncryptedSync('invalidIntegrations', invalidIntegrations);
  secureStorage.setEncryptedSync('isAdminUser', isAdmin);

  logger.info('Stored invalid integrations and admin status in encrypted storage', { isAdmin });

  // Use timeout to ensure state is saved
  setTimeout(() => {
    window.location.href = '/organization-settings';
  }, NAVIGATION_CONSTANTS.NAVIGATION_DELAY_MS);

  // Also use navigate as fallback (state in memory is fine - not persisted)
  navigate('/organization-settings', {
    replace: true,
    state: {
      invalidCredentials: true,
      invalidIntegrations,
      isAdmin
    }
  });
};
